import { Component, OnDestroy, OnInit } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { forkJoin, of } from 'rxjs';
import Swal from 'sweetalert2';
import { baseUrl } from '../../../constants';
import { AuthService } from '../../../core/auth.service';
import { StaffService } from '../staff.service';

@Component({
  selector: 'app-staff-messages',
  templateUrl: './staff-messages.component.html',
  styleUrls: ['./staff-messages.component.scss']
})
export class StaffMessagesComponent implements OnInit, OnDestroy {
  loading = false;
  inbox: any[] = [];
  sent: any[] = [];
  staffOptions: any[] = [];
  activeMailbox: 'inbox' | 'sent' = 'inbox';
  selectedMessage: any | null = null;
  searchTerm = '';
  replyText = '';
  showCompose = false;
  managingRecipients = false;
  managedRecipientStaffIds: number[] = [];
  private notificationConnection?: signalR.HubConnection;

  form = {
    subject: '',
    body: '',
    sendToAllStaff: false,
    recipientStaffIds: [] as number[]
  };
  attachment: { name: string; contentType: string; dataUrl: string } | null = null;
  replyAttachment: { name: string; contentType: string; dataUrl: string } | null = null;

  constructor(
    private staffService: StaffService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.loadPage();
    this.connectNotifications();
  }

  async ngOnDestroy(): Promise<void> {
    await this.notificationConnection?.stop();
  }

  loadPage(): void {
    this.loading = true;
    forkJoin({
      inbox: this.canViewMailbox() ? this.staffService.getInboxMessages() : of([]),
      sent: this.canViewMailbox() ? this.staffService.getSentMessages() : of([]),
      staff: this.canSendMessages() ? this.staffService.getAll({ isActive: true }) : of([]),
      markReplyAlertsRead: this.canViewMailbox() ? this.staffService.markReplyAlertsRead() : of(null)
    }).subscribe({
      next: ({ inbox, sent, staff }) => {
        this.inbox = inbox || [];
        this.sent = sent || [];
        this.staffOptions = staff || [];
        this.restoreSelection();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load staff messages.', 'error');
      }
    });
  }

  canSendGeneral(): boolean {
    return this.auth.isAdmin() || this.auth.hasPermission('staff.message.all') || this.auth.hasPermission('staff.manage');
  }

  canSendMessages(): boolean {
    return this.auth.hasAnyPermission(['staff.message.send', 'staff.message.all', 'staff.manage']);
  }

  sendMessage(): void {
    const subject = String(this.form.subject || '').trim();
    const body = String(this.form.body || '').trim();

    if (!subject || !body) {
      Swal.fire('Required', 'Message subject and body are required.', 'info');
      return;
    }

    if (!this.form.sendToAllStaff && !this.form.recipientStaffIds.length) {
      Swal.fire('Required', 'Select at least one recipient.', 'info');
      return;
    }

    if (this.form.sendToAllStaff && !this.canSendGeneral()) {
      Swal.fire('Not Allowed', 'Only HR/Admin can send to all staff.', 'warning');
      return;
    }

    this.staffService.sendMessage({
      subject,
      body,
      sendToAllStaff: this.form.sendToAllStaff,
      recipientStaffIds: this.form.sendToAllStaff ? [] : this.form.recipientStaffIds,
      attachmentName: this.attachment?.name || null,
      attachmentContentType: this.attachment?.contentType || null,
      attachmentDataUrl: this.attachment?.dataUrl || null
    }).subscribe({
      next: () => {
        this.form = {
          subject: '',
          body: '',
          sendToAllStaff: false,
          recipientStaffIds: []
        };
        this.attachment = null;
        this.showCompose = false;
        Swal.fire('Sent', 'Message sent successfully.', 'success');
        this.loadPage();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to send message.', 'error')
    });
  }

  setMailbox(mailbox: 'inbox' | 'sent'): void {
    this.activeMailbox = mailbox;
    this.selectedMessage = null;
    this.replyText = '';
    this.restoreSelection();
  }

  get mailboxItems(): any[] {
    const source = this.activeMailbox === 'inbox' ? this.inbox : this.sent;
    const q = this.searchTerm.trim().toLowerCase();
    if (!q) return source;

    return source.filter((m: any) => {
      const hay = [
        m.subject,
        m.body,
        m.senderName,
        ...(m.recipients || []).map((r: any) => r.staffName)
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }

  selectMessage(message: any): void {
    this.selectedMessage = message;
    this.replyText = '';

    if (this.activeMailbox === 'inbox' && message && !message.isRead) {
      this.staffService.markMessageRead(message.recipientId).subscribe({
        next: () => {
          message.isRead = true;
        },
        error: () => {}
      });
    }
  }

  sendReplyInView(): void {
    if (!this.selectedMessage || this.selectedMessage.isClosed) return;

    const value = String(this.replyText || '').trim();
    if (!value && !this.replyAttachment) {
      Swal.fire('Required', 'Reply cannot be empty unless you attach a file.', 'info');
      return;
    }

    this.staffService.replyToMessage(this.selectedMessage.messageId, {
      replyText: value || (this.replyAttachment ? `Shared ${this.replyAttachment.name}` : ''),
      attachmentName: this.replyAttachment?.name || null,
      attachmentContentType: this.replyAttachment?.contentType || null,
      attachmentDataUrl: this.replyAttachment?.dataUrl || null
    }).subscribe({
      next: () => {
        this.replyText = '';
        this.replyAttachment = null;
        Swal.fire('Sent', 'Reply submitted.', 'success');
        this.loadPage();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to submit reply.', 'error')
    });
  }

  closeThread(message?: any): void {
    const row = message || this.selectedMessage;
    if (!row) return;

    if (row.isClosed) {
      Swal.fire('Thread Closed', 'This thread has already been closed.', 'info');
      return;
    }

    Swal.fire({
      title: 'Close this thread?',
      text: 'No one will be able to add more replies after closing.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Close Thread'
    }).then((res) => {
      if (!res.isConfirmed) return;
      this.staffService.closeThread(row.messageId).subscribe({
        next: () => {
          Swal.fire('Closed', 'Thread closed successfully.', 'success');
          this.loadPage();
        },
        error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to close thread.', 'error')
      });
    });
  }

  sentReplyCount(row: any): number {
    return (row.replies || []).length;
  }

  inboxUnreadCount(): number {
    return (this.inbox || []).filter(x => !x.isRead).length;
  }

  formatWhen(value: string): string {
    const date = new Date(value);
    return date.toLocaleString();
  }

  senderLabel(row: any): string {
    if (this.activeMailbox === 'inbox') return row.senderName || 'System';
    if (row.isGeneralNotification) return 'All Staff';
    const count = (row.recipients || []).length;
    return `To ${count} recipient${count === 1 ? '' : 's'}`;
  }

  isSelected(row: any): boolean {
    return !!this.selectedMessage && this.selectedMessage.messageId === row.messageId;
  }

  threadCount(row: any): number {
    return (row?.replies || []).length;
  }

  toggleManageRecipients(): void {
    if (!this.selectedMessage || this.activeMailbox !== 'sent') return;
    this.managingRecipients = !this.managingRecipients;
    this.managedRecipientStaffIds = (this.selectedMessage.recipients || []).map((r: any) => r.staffId);
  }

  saveRecipients(): void {
    if (!this.selectedMessage || !this.managedRecipientStaffIds.length) {
      Swal.fire('Required', 'Select at least one group member.', 'info');
      return;
    }

    this.staffService.updateMessageRecipients(this.selectedMessage.messageId, this.managedRecipientStaffIds).subscribe({
      next: () => {
        this.managingRecipients = false;
        Swal.fire('Updated', 'Group members updated.', 'success');
        this.loadPage();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to update group members.', 'error')
    });
  }

  onAttachmentSelected(event: Event, target: 'compose' | 'reply'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire('File Too Large', 'Please select a file below 2MB for now.', 'info');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const attachment = {
        name: file.name,
        contentType: file.type || 'application/octet-stream',
        dataUrl: String(reader.result || '')
      };
      if (target === 'compose') this.attachment = attachment;
      else this.replyAttachment = attachment;
    };
    reader.readAsDataURL(file);
  }

  removeAttachment(target: 'compose' | 'reply'): void {
    if (target === 'compose') this.attachment = null;
    else this.replyAttachment = null;
  }

  isImageAttachment(row: any): boolean {
    return String(row?.attachmentContentType || '').startsWith('image/');
  }

  toggleCompose(): void {
    if (!this.canSendMessages()) {
      return;
    }
    this.showCompose = !this.showCompose;
  }

  private restoreSelection(): void {
    const currentId = this.selectedMessage?.messageId;
    const source = this.activeMailbox === 'inbox' ? this.inbox : this.sent;
    if (!source.length) {
      this.selectedMessage = null;
      return;
    }

    const match = currentId ? source.find((x: any) => x.messageId === currentId) : null;
    this.selectedMessage = match || source[0];
    this.managingRecipients = false;
  }

  private canViewMailbox(): boolean {
    return this.auth.hasAnyPermission(['staff.message.view', 'staff.message.send', 'staff.manage']);
  }

  private connectNotifications(): void {
    const hubUrl = `${baseUrl.replace(/api\/$/i, '')}hubs/notifications`;
    this.notificationConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { accessTokenFactory: () => localStorage.getItem('token') || '' })
      .withAutomaticReconnect()
      .build();

    this.notificationConnection.on('ReceiveNotification', (notification: any) => {
      if (!String(notification?.category || '').toLowerCase().includes('message')) return;
      Swal.fire({
        toast: true,
        position: 'top-end',
        timer: 3500,
        showConfirmButton: false,
        icon: 'info',
        title: notification.title || 'New message',
        text: notification.message || ''
      });
      this.loadPage();
    });

    this.notificationConnection.start().catch(() => {});
  }
}
